// Renders a real 3D snapshot of each level's course geometry to a cached PNG
// (same offscreen-renderer trick as the avatar thumbnails). Built lazily — a
// level only renders the first time its card is shown — and cached forever.
// Falls back to "" (caller shows the emoji) if a mode can't be rendered.
import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { Path } from "../game/engine/Path";
import { buildIceCrackCourse } from "../game/engine/iceCrackDecor";
import { buildDoorDashCourse } from "../game/engine/doorDashDecor";
import { buildRollingTubesCourse } from "../game/engine/rollingTubesDecor";
import { buildCircleSpinnerCourse } from "../game/engine/circleSpinnerDecor";
import { buildDoubleSpinnerArena } from "../game/engine/doubleSpinnerArena";
import { buildMazeRushCourse } from "../game/engine/mazeRushDecor";
import { buildSpaceShipCourse } from "../game/engine/spaceShipDecor";
import { buildSupermarketSweepCourse } from "../game/engine/supermarketSweepDecor";
import { buildBlockClimbCourse } from "../game/engine/blockClimbDecor";
import { buildRiverBoatCourse } from "../game/engine/riverBoatDecor";
import { buildTrainRoofCourse } from "../game/engine/trainRoofDecor";
import { buildHitParadeCannons } from "../game/engine/hitParadeDecor";
import { SoftBox } from "../game/engine/roundedBoxes";
import { isLowQuality } from "../game/engine/quality";

const W = 320, H = 224;
let renderer: THREE.WebGLRenderer | null = null;
let scene: THREE.Scene | null = null;
let camera: THREE.PerspectiveCamera | null = null;

function ensure() {
  if (renderer && scene && camera) return { renderer, scene, camera };
  try {
    const c = document.createElement("canvas");
    c.width = W; c.height = H;
    renderer = new THREE.WebGLRenderer({ canvas: c, antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(1);
    renderer.setSize(W, H, false);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    scene = new THREE.Scene();
    try {
      const pmrem = new THREE.PMREMGenerator(renderer);
      scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
      scene.environmentIntensity = 0.7;
      pmrem.dispose();
    } catch { /* optional */ }
    scene.add(new THREE.HemisphereLight(0xffffff, 0xc8d4ff, 0.95));
    const key = new THREE.DirectionalLight(0xfff2d8, 2.0);
    key.position.set(-6, 10, 8);
    scene.add(key);
    const rim = new THREE.PointLight(0xff8fd0, 2.0, 60);
    rim.position.set(8, 6, -6);
    scene.add(rim);
    camera = new THREE.PerspectiveCamera(40, W / H, 0.1, 400);
    return { renderer, scene, camera };
  } catch {
    return null;
  }
}

/** Build a short course group for the mode, or null if unsupported. */
function buildCourse(mode: string): THREE.Object3D | null {
  try {
    const seed = 7;
    switch (mode) {
      case "hit-parade": {
        // Hit-parade's floor is the World ramp (not a decor builder), so build
        // a representative road following the ramp profile + the cannons.
        const p = new Path(150, seed, "hit-parade");
        const g = new THREE.Group();
        const roadMat = new THREE.MeshStandardMaterial({ color: 0xe7c08a, roughness: 0.7, metalness: 0.03 });
        const edgeMat = new THREE.MeshStandardMaterial({ color: 0xff5fa7, roughness: 0.4 });
        for (let s = 2; s < p.length; s += 4) {
          const y = p.heightAt(s);
          const tile = new THREE.Mesh(new SoftBox(7, 0.5, 4.2), roadMat);
          tile.position.set(0, y - 0.25, s);
          g.add(tile);
          for (const side of [-1, 1] as const) {
            const edge = new THREE.Mesh(new SoftBox(0.5, 0.22, 4.2), edgeMat);
            edge.position.set(side * 3.2, y + 0.05, s);
            g.add(edge);
          }
        }
        g.add(buildHitParadeCannons(p, p.length, 0).group);
        return g;
      }
      case "ice-crack":      return buildIceCrackCourse(new Path(120, seed, "ice-crack")).group;
      case "door-dash":      return buildDoorDashCourse(new Path(120, seed, "door-dash"), seed).group;
      case "rolling-tubes":  return buildRollingTubesCourse(new Path(140, seed, "rolling-tubes")).group;
      case "circle-spinner": return buildCircleSpinnerCourse(new Path(160, seed, "circle-spinner")).group;
      case "double-spinner": return buildDoubleSpinnerArena(new Path(80, seed, "double-spinner")).group;
      case "maze-rush":      return buildMazeRushCourse(new Path(150, seed, "maze-rush")).group;
      case "space-ship":     return buildSpaceShipCourse(new Path(160, seed, "space-ship"), 0).group;
      case "supermarket-sweep": return buildSupermarketSweepCourse(new Path(150, seed, "supermarket-sweep")).group;
      case "block-climb":    return buildBlockClimbCourse(new Path(150, seed, "block-climb")).group;
      case "river-boat":     return buildRiverBoatCourse(new Path(160, seed, "river-boat"), 0).group;
      case "train-roof":     return buildTrainRoofCourse(new Path(220, seed, "train-roof")).group;
      default:               return null;
    }
  } catch {
    return null;
  }
}

const cache = new Map<string, string>();
export function levelThumbnail(mode: string): string {
  const cached = cache.get(mode);
  if (cached != null) return cached;
  // Low tier: skip the offscreen 3D render — the picker falls back to the
  // level emoji, saving a whole WebGL context on weak GPUs.
  if (isLowQuality()) { cache.set(mode, ""); return ""; }
  const env = ensure();
  if (!env) { cache.set(mode, ""); return ""; }
  const obj = buildCourse(mode);
  if (!obj) { cache.set(mode, ""); return ""; }
  const { renderer: r, scene: s, camera: cam } = env;
  s.add(obj);
  // Frame the first slice of the course at a 3/4 hero angle.
  const box = new THREE.Box3().setFromObject(obj);
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  // Look at the START of the course (low s), slightly raised.
  const target = new THREE.Vector3(0, Math.min(center.y, 2), Math.max(box.min.z + 14, box.min.z + size.z * 0.12));
  const span = Math.min(34, Math.max(14, size.x + 14));
  cam.position.set(target.x - span * 0.55, target.y + span * 0.5, target.z - span * 0.9);
  cam.lookAt(target);
  cam.updateProjectionMatrix();
  r.render(s, cam);
  let url = "";
  try { url = r.domElement.toDataURL("image/png"); } catch { url = ""; }
  s.remove(obj);
  obj.traverse((o) => {
    if (o instanceof THREE.Mesh) {
      o.geometry.dispose();
      const m = o.material;
      if (Array.isArray(m)) m.forEach((mm) => mm.dispose());
      else (m as THREE.Material).dispose();
    }
  });
  cache.set(mode, url);
  return url;
}
